import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon, UploadIcon, StarIcon, MoreHorizontalIcon } from 'lucide-react';
interface RoomImage {
  id: string;
  url: string;
  caption: string;
  uploadedBy: string;
  uploadDate: string;
  isActive: boolean;
  isFeatured?: boolean;
}
interface RoomAssetsProps {
  roomId: string;
  roomName: string;
  images: RoomImage[];
  onBack: () => void;
  onUpload: (files: FileList) => void;
  onUpdateImage: (imageId: string, updates: Partial<RoomImage>) => void;
  onDeleteImage: (imageId: string) => void;
}
export function RoomAssets({
  roomId,
  roomName,
  images,
  onBack,
  onUpload,
  onUpdateImage,
  onDeleteImage
}: RoomAssetsProps) {
  const {
    t
  } = useTranslation('rooms');
  const [activeTab, setActiveTab] = useState<'info' | 'media'>('media');
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      onUpload(e.dataTransfer.files);
    }
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onUpload(e.target.files);
    }
  };
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedImages(images.map(img => img.id));
    } else {
      setSelectedImages([]);
    }
  };
  const handleSelectImage = (imageId: string, checked: boolean) => {
    if (checked) {
      setSelectedImages([...selectedImages, imageId]);
    } else {
      setSelectedImages(selectedImages.filter(id => id !== imageId));
    }
  };
  return <div className="space-y-6" data-id="element-10213">
      <div className="flex items-center" data-id="element-10214">
        <button onClick={onBack} className="flex items-center text-gray-600 hover:text-gray-900" data-id="element-10215">
          <ArrowLeftIcon size={18} className="mr-1" data-id="element-10216" />
          <span data-id="element-10217">{t('components.RoomAssets.room_assets')}</span>
        </button>
      </div>
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-10218">
        {/* Tabs */}
        <div className="border-b border-gray-200" data-id="element-10219">
          <nav className="flex" role="tablist" aria-label="Room form sections" data-id="element-10220">
            <button role="tab" aria-selected={activeTab === 'info'} className={`px-6 py-4 text-sm font-medium ${activeTab === 'info' ? 'border-b-2 border-red-500 text-red-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('info')} data-id="element-10221">
              {t('components.EditRoomForm.room_information')}
            </button>
            <button role="tab" aria-selected={activeTab === 'media'} className={`px-6 py-4 text-sm font-medium ${activeTab === 'media' ? 'border-b-2 border-red-500 text-red-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('media')} data-id="element-10222">
              {t('components.EditRoomForm.media_assets')}
            </button>
          </nav>
        </div>
        {/* Media Assets Content */}
        <div className="p-6" data-id="element-10223">
          {/* File Upload Area */}
          <div className={`border-2 border-dashed rounded-lg p-8 mb-6 flex flex-col items-center justify-center ${dragActive ? 'border-red-500 bg-red-50' : 'border-gray-300'}`} onDragEnter={handleDrag} onDragOver={handleDrag} onDragLeave={handleDrag} onDrop={handleDrop} data-id="element-10224">
            <div className="p-3 bg-red-100 rounded-full mb-4" data-id="element-10225">
              <UploadIcon size={24} className="text-red-500" data-id="element-10226" />
            </div>
            <p className="text-gray-600 mb-1" data-id="element-10227">
              {t('components.RoomAssets.drag_files_here')}
            </p>
            <p className="text-xs text-gray-500 mb-4" data-id="element-10228">
              {t('components.RoomAssets.supported_formats_png_jpg_jpeg')}
            </p>
            <p className="text-xs text-gray-500 mb-4" data-id="element-10229">
              {t('components.RoomAssets.max_file_size_10mb')}
            </p>
            <label className="cursor-pointer" data-id="element-10230">
              <input type="file" accept="image/png,image/jpeg,image/jpg" className="hidden" onChange={handleFileChange} multiple data-id="element-10231" />

              <span className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700" data-id="element-10232">
                {t('components.RoomAssets.upload')}
              </span>
            </label>
          </div>
          {/* Images Table */}
          <div className="overflow-x-auto" data-id="element-10233">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-10234">
              <thead data-id="element-10235">
                <tr data-id="element-10236">
                  <th className="w-12 py-3 pl-4" data-id="element-10237">
                    <input type="checkbox" checked={selectedImages.length === images.length && images.length > 0} onChange={handleSelectAll} className="h-4 w-4 text-red-600 border-gray-300 rounded" data-id="element-10238" />
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10239">
                    {t('components.RoomAssets.room_image')}
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10240">
                    {t('components.RoomAssets.caption')}
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10241">
                    {t('components.RoomAssets.uploaded_by')}
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10242">
                    {t('components.RoomAssets.uploaded')}
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10243">
                    {t('components.RoomAssets.visibility')}
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10244">
                    {t('components.RoomAssets.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-10245">
                {images.map(image => <tr key={image.id} data-id="element-10246">
                    <td className="py-4 pl-4" data-id="element-10247">
                      <input type="checkbox" checked={selectedImages.includes(image.id)} onChange={e => handleSelectImage(image.id, e.target.checked)} className="h-4 w-4 text-red-600 border-gray-300 rounded" data-id="element-10248" />
                    </td>
                    <td className="py-4" data-id="element-10249">
                      <div className="w-24 h-16 bg-gray-100 rounded overflow-hidden" data-id="element-10250">
                        <img src={image.url} alt={image.caption || 'Room image'} className="w-full h-full object-cover" data-id="element-10251" />
                      </div>
                    </td>
                    <td className="py-4" data-id="element-10252">
                      <div className="flex items-center" data-id="element-10253">
                        {image.isFeatured && <StarIcon size={16} className="text-yellow-400 mr-1" data-id="element-10254" />}
                        <span data-id="element-10255">{image.caption || 'Untitled'}</span>
                      </div>
                    </td>
                    <td className="py-4 text-sm text-gray-500" data-id="element-10256">
                      {image.uploadedBy}
                    </td>
                    <td className="py-4 text-sm text-gray-500" data-id="element-10257">
                      {image.uploadDate}
                    </td>
                    <td className="py-4" data-id="element-10258">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${image.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-10259">
                        {image.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-4" data-id="element-10260">
                      <button aria-label="Image actions" className="text-gray-400 hover:text-gray-500" data-id="element-10261">
                        <MoreHorizontalIcon size={16} aria-hidden="true" data-id="element-10262" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>;
}